/**
 * TOSS Web Application - Messenger SignalR Hook
 *
 * React hook for real-time messenger updates via SignalR.
 * Connects to /hubs/messenger and handles all messenger events.
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useMessengerStore } from '@/store/messengerStore';
import { useUIStore } from '@/store/uiStore';
import {
  addMessageToCache,
  updateMessageInCache,
  updatePinnedMessageInCache,
  invalidateMessengerChats,
  isChatInCachedLists,
  updateMessagesByFileIdInCache,
} from '@/hooks/api/useMessenger';
import { messengerApi } from '@/api/messenger.api';
import { queryKeys } from '@/app/queryClient';
import type {
  ChatMessage,
  ChatFull,
  ChatListResponse,
  MessageDeletedEvent,
  MessageListResponse,
  MessagePinnedEvent,
  MessageReactionsChangedEvent,
  UserTypingEvent,
  StopTypingEvent,
  MessageReadEvent,
  MemberAddedEvent,
  MemberRemovedEvent,
  MemberRoleChangedEvent,
  ChatDeletedEvent,
  UnreadCountChangedEvent,
  ChatUnreadIncrementedEvent,
  ChatShort,
  UnreadCountResponse,
  FileProcessedEvent,
} from '@/types/messenger.types';
import { MessengerHubMethods } from '@/types/messenger.types';
import type { SignalRConnectionState } from '@/types';
import { getMessengerHub } from './messengerHubManager';

// ============================================================================
// Hook
// ============================================================================

export interface UseMessengerSignalRResult {
  connectionState: SignalRConnectionState;
  isConnected: boolean;
}

export function useMessengerSignalR(): UseMessengerSignalRResult {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setTypingUser = useMessengerStore((state) => state.setTypingUser);
  const removeTypingUser = useMessengerStore((state) => state.removeTypingUser);
  const addOnlineUser = useMessengerStore((state) => state.addOnlineUser);
  const removeOnlineUser = useMessengerStore((state) => state.removeOnlineUser);
  const setOnlineUsers = useMessengerStore((state) => state.setOnlineUsers);

  const addToast = useUIStore((state) => state.addToast);
  const navigate = useNavigate();

  const [connectionState, setConnectionState] = useState<SignalRConnectionState>('Disconnected');
  const hub = getMessengerHub();

  // Debounced markAllAsRead timers, keyed by chatId. When the active chat
  // receives a burst of incoming messages we don't want to fire a separate
  // markAllAsRead HTTP request for each one — coalesce them into a single
  // call 400ms after the last message. The key is per-chat so switching
  // chats mid-burst still fires the pending call for the previous chat
  // (otherwise its LastReadAt wouldn't advance and the global unreadCount
  // would be wrong).
  const markAllAsReadTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Tracks active typing-auto-remove timers so we can clear them on unmount.
  // Map key = `${chatId}:${userId}`.
  const typingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Handle incoming new message (backend sends ChatMessageDto directly)
  const handleReceiveMessage = useCallback(
    (message: ChatMessage) => {
      const currentUserId = useAuthStore.getState().user?.id;

      // Skip own messages — they are handled by the optimistic update flow
      // (useSendMessage.onMutate adds temp message, onSuccess replaces it with real).
      // Adding here would cause a brief duplicate until the HTTP response arrives.
      if (message.senderId === currentUserId) return;

      addMessageToCache(queryClient, message, currentUserId);

      // addMessageToCache only PATCHES a chat already in the cached list (it .map()s existing
      // rows). A chat hidden by "delete for me" — or created elsewhere and never fetched — is
      // absent from every cached page, so the patch is a no-op for it and it would otherwise stay
      // hidden until the next 30s refetch. Fall back to a real refetch for exactly that case.
      if (!isChatInCachedLists(queryClient, message.chatId)) {
        invalidateMessengerChats(queryClient);
      }

      const activeChatId = useMessengerStore.getState().activeChatId;

      // Show clickable toast if not viewing this chat, not own message, not muted, not mobile
      if (message.senderId !== currentUserId && message.chatId !== activeChatId) {
        const isMobile = window.innerWidth < 768;
        // Check if chat is muted from query cache
        const chatListData = queryClient.getQueriesData<ChatListResponse>({ queryKey: queryKeys.messenger.chats() });
        const isMuted = chatListData.some(([, data]) =>
          data?.items?.some((c) => c.id === message.chatId && c.isMuted)
        );

        if (!isMuted && !isMobile) {
          addToast({
            type: 'info',
            title: message.senderName,
            message: message.content ?? 'Файл',
            onClick: () => navigate(`/messenger/${message.chatId}`),
          });
        }
      }

      // If the user is currently viewing this chat, mark as read so the
      // backend updates LastReadAt and the global unreadCount stays correct.
      // Debounced to 400ms so a rapid burst of messages produces a single
      // HTTP call. Raw API is used intentionally: useMarkAllAsRead's
      // optimistic onMutate cannot be triggered from a SignalR callback
      // (non-React context), but for the active chat the cache is already
      // correct — addMessageToCache does not increment unreadCount for the
      // active chat, so no optimistic update is needed here. The server
      // response is the thing we care about (LastReadAt).
      if (message.chatId === activeChatId && message.senderId !== currentUserId) {
        const chatIdForClosure = message.chatId;
        const existing = markAllAsReadTimersRef.current.get(chatIdForClosure);
        if (existing) clearTimeout(existing);
        const timer = setTimeout(() => {
          markAllAsReadTimersRef.current.delete(chatIdForClosure);
          messengerApi.markAllAsRead(chatIdForClosure).catch(() => {});
        }, 400);
        markAllAsReadTimersRef.current.set(chatIdForClosure, timer);
      }
    },
    [queryClient, addToast, navigate]
  );

  // Handle edited message (backend sends ChatMessageDto directly)
  const handleMessageEdited = useCallback(
    (message: ChatMessage) => {
      updateMessageInCache(queryClient, message.chatId, message.id, {
        content: message.content,
        isEdited: message.isEdited,
      });
    },
    [queryClient]
  );

  // Video release: server-side compression finished (or the source was kept) for a file attached
  // to one or more messages of this chat — patch every message naming that file in place. No
  // `isChatInCachedLists` fallback here (unlike `handleReceiveMessage` below): this never ADDS a
  // row, it only patches messages already in a cached page, which a chat the user has open (or has
  // paged through before) already has.
  //
  // Since 25.09.2026 the patch also carries the poster, the length, the display size and `Ready`,
  // so a tile swaps its spinner or first frame for the server's poster without a refetch (variant
  // A). A server that predates `processingStatus` on the event still means `Ready` by sending it at
  // all; the length, width and height are written only when sent, never cleared by their absence —
  // an event with `durationSeconds: null` used to wipe a length the message already showed.
  // (`posterUrl` is written as it comes: its absence after processing means there is no poster.)
  const handleFileProcessed = useCallback(
    (event: FileProcessedEvent) => {
      updateMessagesByFileIdInCache(queryClient, event.chatId, event.fileId, {
        fileName: event.fileName,
        fileContentType: event.contentType,
        fileSize: event.size,
        posterUrl: event.posterUrl,
        processingStatus: event.processingStatus ?? 'Ready',
        ...(event.durationSeconds != null ? { durationSeconds: event.durationSeconds } : {}),
        ...(event.width != null ? { width: event.width } : {}),
        ...(event.height != null ? { height: event.height } : {}),
      });
    },
    [queryClient]
  );

  // Handle deleted message
  const handleMessageDeleted = useCallback(
    (event: MessageDeletedEvent) => {
      updateMessageInCache(queryClient, event.chatId, event.messageId, {
        isDeleted: true,
        content: undefined,
      });

      // Update chat list sidebar with new last message preview
      queryClient.setQueriesData<ChatListResponse>(
        { queryKey: queryKeys.messenger.chats() },
        (oldData) => {
          if (!oldData?.items) return oldData;
          return {
            ...oldData,
            items: oldData.items.map((chat) =>
              chat.id === event.chatId
                ? {
                    ...chat,
                    lastMessage: event.lastMessagePreview ?? undefined,
                    lastMessageAt: event.lastMessageAt ?? undefined,
                  }
                : chat
            ),
          };
        }
      );

      // Fallback: invalidate chat list to refetch from server
      invalidateMessengerChats(queryClient);
    },
    [queryClient]
  );

  // Handle user typing
  const handleUserTyping = useCallback(
    (event: UserTypingEvent) => {
      setTypingUser(event.chatId, {
        userId: event.userId,
        fullName: event.userName,
        timestamp: Date.now(),
      });

      // Auto-remove after 5 seconds if no stop event. Track the timer so we
      // can clear it on unmount (and reset it if the same user keeps typing
      // so we don't accumulate stale timers in the map).
      const key = `${event.chatId}:${event.userId}`;
      const existing = typingTimeoutsRef.current.get(key);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        removeTypingUser(event.chatId, event.userId);
        typingTimeoutsRef.current.delete(key);
      }, 5000);
      typingTimeoutsRef.current.set(key, timer);
    },
    [setTypingUser, removeTypingUser]
  );

  // Handle user stopped typing
  const handleUserStoppedTyping = useCallback(
    (event: StopTypingEvent) => {
      removeTypingUser(event.chatId, event.userId);
    },
    [removeTypingUser]
  );

  // Handle message read (update member's lastReadMessageId in chat detail cache)
  const handleMessageRead = useCallback(
    (event: MessageReadEvent) => {
      queryClient.setQueryData<ChatFull>(
        queryKeys.messenger.chat(event.chatId),
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            members: oldData.members.map((m) =>
              m.userId === event.userId
                ? { ...m, lastReadMessageId: event.messageId }
                : m
            ),
          };
        }
      );

      // Multi-device sync: if the current user marked messages read from
      // another device/tab, reset the per-chat unreadCount in the chat list
      // cache so the sidebar badge drops without waiting for a refetch.
      const currentUserId = useAuthStore.getState().user?.id;
      if (event.userId === currentUserId) {
        queryClient.setQueriesData<ChatListResponse>(
          { queryKey: queryKeys.messenger.chats() },
          (oldData) => {
            if (!oldData?.items) return oldData;
            return {
              ...oldData,
              items: oldData.items.map((chat) =>
                chat.id === event.chatId ? { ...chat, unreadCount: 0 } : chat
              ),
            };
          }
        );
      }
    },
    [queryClient]
  );

  // Handle chat created
  const handleChatCreated = useCallback(
    (chat: ChatShort) => {
      invalidateMessengerChats(queryClient);
      if (hub.isConnected) {
        hub.invoke('JoinChat', chat.id).catch(() => {});
      }
    },
    [queryClient, hub]
  );

  // Handle chat updated
  const handleChatUpdated = useCallback(
    (_chat: ChatShort) => {
      invalidateMessengerChats(queryClient);
    },
    [queryClient]
  );

  // Handle member added
  const handleMemberAdded = useCallback(
    (event: MemberAddedEvent) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chat(event.chatId) });
      invalidateMessengerChats(queryClient);
      const currentUserId = useAuthStore.getState().user?.id;
      if (event.member.userId === currentUserId && hub.isConnected) {
        hub.invoke('JoinChat', event.chatId).catch(() => {});
      }
    },
    [queryClient, hub]
  );

  // Handle unread count changed
  const handleUnreadCountChanged = useCallback(
    (event: UnreadCountChangedEvent) => {
      queryClient.setQueryData<UnreadCountResponse>(
        queryKeys.messenger.unreadCount(),
        { count: event.totalUnread }
      );
    },
    [queryClient]
  );

  // Handle per-chat unread increment (additive event from backend Commit 2).
  // Updates the chat list sidebar badge for one specific chat without
  // touching the global unread count (which is still delivered via
  // UnreadCountChanged for backward compatibility).
  const handleChatUnreadIncremented = useCallback(
    (event: ChatUnreadIncrementedEvent) => {
      const currentUserId = useAuthStore.getState().user?.id;
      const activeChatId = useMessengerStore.getState().activeChatId;

      // Do not bump the badge for a chat the user is currently viewing —
      // handleReceiveMessage will debounce a markAllAsRead for it and the
      // backend will broadcast MessageRead / UnreadCountChanged back.
      if (event.chatId === activeChatId) return;

      // Defensive: if our own userId triggered this (shouldn't happen —
      // backend excludes the sender — but guard against race/bug), ignore.
      if (event.byUserId === currentUserId) return;

      queryClient.setQueriesData<ChatListResponse>(
        { queryKey: queryKeys.messenger.chats() },
        (oldData) => {
          if (!oldData?.items) return oldData;
          return {
            ...oldData,
            items: oldData.items.map((chat) =>
              chat.id === event.chatId
                ? { ...chat, unreadCount: event.newChatUnreadCount }
                : chat
            ),
          };
        }
      );

      // Same rule as handleReceiveMessage: a chat absent from every cached list page (hidden, or
      // never fetched) cannot be patched into existing — refetch instead.
      if (!isChatInCachedLists(queryClient, event.chatId)) {
        invalidateMessengerChats(queryClient);
      }
    },
    [queryClient]
  );

  // Handle member removed
  const handleMemberRemoved = useCallback(
    (event: MemberRemovedEvent) => {
      const currentUserId = useAuthStore.getState().user?.id;

      // Direct cache update: remove member from chat detail
      queryClient.setQueryData<ChatFull>(
        queryKeys.messenger.chat(event.chatId),
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            members: oldData.members.filter((m) => m.userId !== event.userId),
          };
        }
      );

      // Direct cache update: remove member from all chat list caches
      queryClient.setQueriesData<ChatListResponse>(
        { queryKey: queryKeys.messenger.chats() },
        (oldData) => {
          if (!oldData?.items) return oldData;
          return {
            ...oldData,
            items: oldData.items.map((chat) =>
              chat.id === event.chatId
                ? { ...chat, members: chat.members.filter((m) => m.userId !== event.userId) }
                : chat
            ),
          };
        }
      );

      // Fallback: invalidate queries to refetch from server
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chat(event.chatId) });
      invalidateMessengerChats(queryClient);

      if (event.userId === currentUserId) {
        const { activeChatId, setActiveChatId } = useMessengerStore.getState();
        if (activeChatId === event.chatId) {
          setActiveChatId(null);
        }
      }
    },
    [queryClient]
  );

  // Handle member role changed
  const handleMemberRoleChanged = useCallback(
    (event: MemberRoleChangedEvent) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chat(event.chatId) });
      invalidateMessengerChats(queryClient);
    },
    [queryClient]
  );

  // Handle chat deleted
  const handleChatDeleted = useCallback(
    (event: ChatDeletedEvent) => {
      const { activeChatId, setActiveChatId } = useMessengerStore.getState();
      if (activeChatId === event.chatId) {
        setActiveChatId(null);
      }
      queryClient.removeQueries({ queryKey: queryKeys.messenger.chat(event.chatId) });
      queryClient.removeQueries({ queryKey: queryKeys.messenger.messages(event.chatId) });
      invalidateMessengerChats(queryClient);
    },
    [queryClient]
  );

  // Handle message pinned/unpinned
  const handleMessagePinned = useCallback(
    (event: MessagePinnedEvent) => {
      updatePinnedMessageInCache(queryClient, event.chatId, event.pinnedMessage);
      invalidateMessengerChats(queryClient);
    },
    [queryClient]
  );

  /**
   * Реакции сообщения изменились у кого-то другого.
   *
   * Правится ровно одно сообщение в кэше, без запроса: событие несёт ПОЛНЫЙ набор. Поле
   * `reactedByMe` в рассылке всегда `false` — на момент отправки читателя не существует, — поэтому
   * своё сохраняем из того, что уже лежит в кэше: его туда положил ответ собственной мутации.
   */
  const handleMessageReactions = useCallback(
    (event: MessageReactionsChangedEvent) => {
      if (!event?.chatId || !event.messageId) return;

      queryClient.setQueryData<{ pages: MessageListResponse[]; pageParams: unknown[] }>(
        queryKeys.messenger.messages(event.chatId),
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: (page.items ?? []).map((message) =>
                message.id === event.messageId
                  ? {
                      ...message,
                      reactions: (event.reactions ?? []).map((reaction) => ({
                        ...reaction,
                        reactedByMe:
                          message.reactions?.find((mine) => mine.emoji === reaction.emoji)
                            ?.reactedByMe ?? false,
                      })),
                    }
                  : message
              ),
            })),
          };
        }
      );
    },
    [queryClient]
  );

  // Handle user online
  const handleUserOnline = useCallback(
    (event: { userId: string }) => {
      addOnlineUser(event.userId);
    },
    [addOnlineUser]
  );

  // Handle user offline
  const handleUserOffline = useCallback(
    (event: { userId: string }) => {
      removeOnlineUser(event.userId);
    },
    [removeOnlineUser]
  );

  // Setup connection state listener
  useEffect(() => {
    const unsubscribe = hub.onStateChange((state) => {
      setConnectionState(state);
      useMessengerStore.getState().setConnectionState(state);
    });
    return unsubscribe;
  }, [hub]);

  // Register event handlers (decoupled from connectionState).
  // Handlers are stored in SignalRManager's subscription registry and survive
  // auto-reconnect and manual reconnect (replayed onto new connections).
  useEffect(() => {
    const unsubs = [
      hub.on<ChatMessage>(MessengerHubMethods.ReceiveMessage, handleReceiveMessage),
      hub.on<ChatMessage>(MessengerHubMethods.MessageEdited, handleMessageEdited),
      hub.on<MessageDeletedEvent>(MessengerHubMethods.MessageDeleted, handleMessageDeleted),
      hub.on<UserTypingEvent>(MessengerHubMethods.UserTyping, handleUserTyping),
      hub.on<StopTypingEvent>(MessengerHubMethods.UserStoppedTyping, handleUserStoppedTyping),
      hub.on<MessageReadEvent>(MessengerHubMethods.MessageRead, handleMessageRead),
      hub.on<ChatShort>(MessengerHubMethods.ChatCreated, handleChatCreated),
      hub.on<ChatShort>(MessengerHubMethods.ChatUpdated, handleChatUpdated),
      hub.on<MemberAddedEvent>(MessengerHubMethods.MemberAdded, handleMemberAdded),
      hub.on<MemberRemovedEvent>(MessengerHubMethods.MemberRemoved, handleMemberRemoved),
      hub.on<MemberRoleChangedEvent>(MessengerHubMethods.MemberRoleChanged, handleMemberRoleChanged),
      hub.on<ChatDeletedEvent>(MessengerHubMethods.ChatDeleted, handleChatDeleted),
      hub.on<UnreadCountChangedEvent>(MessengerHubMethods.UnreadCountChanged, handleUnreadCountChanged),
      hub.on<ChatUnreadIncrementedEvent>(MessengerHubMethods.ChatUnreadIncremented, handleChatUnreadIncremented),
      hub.on<MessagePinnedEvent>(MessengerHubMethods.MessagePinned, handleMessagePinned),
      hub.on<MessageReactionsChangedEvent>(
        MessengerHubMethods.MessageReactionsChanged,
        handleMessageReactions
      ),
      hub.on<{ userId: string }>('UserOnline', handleUserOnline),
      hub.on<{ userId: string }>('UserOffline', handleUserOffline),
      hub.on<FileProcessedEvent>(MessengerHubMethods.FileProcessed, handleFileProcessed),
    ];

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [
    hub,
    handleReceiveMessage,
    handleMessageEdited,
    handleMessageDeleted,
    handleUserTyping,
    handleUserStoppedTyping,
    handleMessageRead,
    handleChatCreated,
    handleChatUpdated,
    handleMemberAdded,
    handleMemberRemoved,
    handleMemberRoleChanged,
    handleChatDeleted,
    handleUnreadCountChanged,
    handleChatUnreadIncremented,
    handleMessagePinned,
    handleUserOnline,
    handleUserOffline,
    handleFileProcessed,
  ]);

  // Clean up all debounced timers on unmount so we don't leak timers
  // (markAllAsRead coalescing + typing auto-remove).
  useEffect(() => {
    const markTimers = markAllAsReadTimersRef.current;
    const typingTimers = typingTimeoutsRef.current;
    return () => {
      for (const t of markTimers.values()) clearTimeout(t);
      markTimers.clear();
      for (const t of typingTimers.values()) clearTimeout(t);
      typingTimers.clear();
    };
  }, []);

  // Catch up on missed events after (re)connection.
  // Invalidates TanStack Query caches so any data missed during the
  // disconnection window is fetched from the server.
  useEffect(() => {
    if (connectionState !== 'Connected') return;

    // Invalidate chat list and unread count to pick up missed events
    invalidateMessengerChats(queryClient);
    queryClient.invalidateQueries({ queryKey: queryKeys.messenger.unreadCount() });

    // If viewing a specific chat, refetch its data too
    const activeChatId = useMessengerStore.getState().activeChatId;
    if (activeChatId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.messages(activeChatId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.messenger.chat(activeChatId) });
      messengerApi.markAllAsRead(activeChatId).catch(() => {});
    }

    // Fetch online users on connect
    messengerApi.getOnlineUsers().then(setOnlineUsers).catch(() => {});
  }, [connectionState, queryClient, setOnlineUsers]);

  // Connect when authenticated (singleton hub — don't disconnect on unmount,
  // only disconnect when user logs out i.e. isAuthenticated becomes false)
  useEffect(() => {
    if (isAuthenticated) {
      hub.startConnection();
    } else {
      hub.stopConnection();
    }
  }, [isAuthenticated, hub]);

  return {
    connectionState,
    isConnected: connectionState === 'Connected',
  };
}

export default useMessengerSignalR;
